<script setup lang="ts">
/**
 * อ่านเมนูจากแชท — the action under a customer's bubble (owner 28 ก.ย. 69):
 * - text: on the last bubble of a run of customer texts that names ≥ 2 menus / P codes, or 1 with a quantity
 *   (lib/menuRead.ts, local matching, no AI cost) → "อ่านเมนูนี้";
 * - picture: every customer picture → "อ่านเมนูจากรูปนี้" (the AI runs only when pressed).
 * Pressing opens the recheck modal (MenuReadModal.vue) and reads at once. Hidden while the owner has "อ่านเมนูจากแชท"
 * switched off (Config › AI; its own switch since 28 ก.ย. — "หยุด AI ตอบเองทั้งหมด" does not hide it).
 */
import { computed, onMounted } from 'vue'
import type { ChatMessage } from '../../api/types'
import { imageSource, textSource } from '../../lib/menuRead'
import { useInbox } from '../../stores/inbox'
import { useMenuRead } from '../../stores/menuRead'
import { useOrders } from '../../stores/order'
import Icon from '../ui/Icon.vue'

const props = defineProps<{ item: ChatMessage }>()
const inbox = useInbox()
const orders = useOrders()
const mr = useMenuRead()

const detail = computed(() => inbox.details[props.item.conversation_id] ?? null)
const live = computed(() => !!detail.value && !detail.value.is_history && detail.value.kind === 'dm')
const source = computed(() => {
  if (!live.value || mr.enabled === false) return null
  const thread = inbox.threads[props.item.conversation_id] ?? []
  if (props.item.type === 'image') return props.item.attachments.some((a) => a.type === 'image') ? imageSource(thread, props.item) : null
  const menus = orders.catalog?.menus ?? []
  return menus.length ? textSource(thread, props.item, menus) : null
})
const busy = computed(() => mr.busyFor(props.item.id))

onMounted(() => {
  if (live.value) void mr.checkEnabled()
  // the local matcher needs the POS menu names (cached once per page; the order tab loads the same list)
  // (a POS that did not answer is not asked again by every bubble: the order tab retries on open)
  if (props.item.type === 'text' && live.value && !orders.catalog && !orders.catalogError) void orders.loadCatalog()
})
</script>

<template>
  <div v-if="source" class="mra">
    <span v-if="busy" class="mra-busy" role="status"><span class="mra-dot" aria-hidden="true"></span>กำลังอ่านเมนู…</span>
    <button
      v-else
      type="button"
      class="mra-b"
      :title="source.kind === 'image' ? 'ให้ AI อ่านเมนูในรูปนี้ แล้วตรวจก่อนลงออเดอร์' : 'ให้ AI อ่านเมนูในข้อความนี้ แล้วตรวจก่อนลงออเดอร์'"
      @click="mr.start(source)"
    >
      <Icon name="utensils" />{{ source.kind === 'image' ? 'อ่านเมนูจากรูปนี้' : source.ids.length > 1 ? `อ่านเมนูนี้ (${source.ids.length} ข้อความ)` : 'อ่านเมนูนี้' }}
    </button>
  </div>
</template>

<style scoped>
.mra {
  display: flex;
  padding: 0 2px;
}
.mra-b {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  height: 26px;
  padding: 0 10px 0 8px;
  border: 1px solid var(--line);
  border-radius: 999px;
  background: #fff;
  color: var(--ac2);
  font-size: 12.5px;
  font-weight: 600;
  line-height: 19px;
  white-space: nowrap;
}
.mra-b:hover {
  background: var(--acs);
  border-color: #bfdccd;
}
.mra-b:focus-visible {
  outline: 2px solid var(--ac);
  outline-offset: 2px;
}
.mra-b :deep(.ic) {
  width: 14px;
  height: 14px;
}
.mra-busy {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  height: 26px;
  font-size: 12.5px;
  line-height: 19px;
  color: var(--mut);
  white-space: nowrap;
}
.mra-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--ac);
  animation: mra-pulse 1s ease-in-out infinite;
}
@keyframes mra-pulse {
  50% {
    opacity: 0.25;
  }
}
@media (prefers-reduced-motion: reduce) {
  .mra-dot {
    animation: none;
  }
}
</style>
